import { Slider as PrimeSlider, SliderChangeParams, SliderValueType } from 'primereact/slider'

import React, { useEffect, useRef } from 'react'
import style from './styles/slider.module.scss'
import useSurveyLocale from '../modules/locale/useSurveyLocale'
import useViewport from '../modules/viewport/useViewport'
import { hapticEffects } from '../lib/hapticEffects'

const SLIDER_BASE = 100

interface SliderProps {
  answerText?: string
  disabled?: boolean
  label?: string
  value: number
  min?: number
  max?: number
  step?: number
  className?: string
  start?: 'center'
  remaining?: number
  transparentBackground?: boolean
  valueFromCenter?: boolean
  updateOnDrag?: boolean
  ticks?: boolean
  labelTemplate?: (value: number, index: number) => React.ReactNode
  onChange: (value: number) => void
  onTouch?: () => void
  onKeyDown?: (value: number) => void
  onSlideEnd?: () => void
}

const convertValue = (event: SliderChangeParams) => {
  if (Array.isArray(event.value)) {
    return event.value[1] / SLIDER_BASE
  }
  return event.value / SLIDER_BASE
}

export default function Slider({
  answerText,
  disabled,
  label,
  value,
  min,
  max,
  step = 1,
  className,
  remaining,
  valueFromCenter,
  start,
  ticks,
  transparentBackground,
  onChange,
  onTouch,
  onSlideEnd,
  labelTemplate
}: SliderProps) {
  const [touched, setTouched] = React.useState(false)
  const [localValue, setLocalValue] = React.useState(value)
  const [localArray, setLocalArray] = React.useState<[number, number]>([0, 0])
  const [localBackUpArray, setLocalBackUpArray] = React.useState<[number, number]>([0, 0])
  const [dragging, setDragging] = React.useState(false)
  const [keyboardChange, setKeyboardChange] = React.useState(false)
  const currentValue = useRef<number>()
  const sliderRef = useRef<HTMLDivElement>(null)
  const { t } = useSurveyLocale()
  const viewport = useViewport()

  useEffect(() => {
    if (ticks) {
      sliderRef.current?.style?.setProperty('--prime-slider-tick-percent', `${100 / (max - min || 1)}%`)
    }
    if (transparentBackground) {
      sliderRef.current?.style?.setProperty('--prime-slider-tick-percent', '50%')
    }
  }, [max, min, ticks, transparentBackground])

  useEffect(() => {
    setLocalValue(value)
    if (currentValue.current) {
      currentValue.current = value
    }
  }, [value])

  if (remaining != null && sliderRef.current)
    sliderRef.current?.style?.setProperty(
      '--prime-slider-remaining-width',
      `${(1 == remaining ? 0 : remaining) * 100}%`
    )

  if (currentValue.current != value && !dragging && !keyboardChange) {
    setDragging(true)
    setTimeout(() => {
      if (!valueFromCenter) setLocalValue(value)
      else {
        currentValue.current = value
        setLocalArray(value < 0 ? [value * SLIDER_BASE, 0] : [0, value * SLIDER_BASE])
      }
      setDragging(false)
    }, 50)
  }

  if (value != null && !touched) setTouched(true)

  const onEndChangeValue = (value: number) => {
    if (value < min || value > max) return
    setDragging(false)
    if ('function' == typeof onSlideEnd) onSlideEnd()
    const fixedValue = Number(
      (Math.round((remaining != null && value > remaining * max ? remaining * max : value) / step) * step).toFixed(2)
    )
    setDragging(true)
    setTimeout(() => {
      setLocalValue(fixedValue)
      currentValue.current = fixedValue

      onChange(fixedValue)
      setDragging(false)
    }, 50)
  }

  const onChangeValue = (event: SliderChangeParams) => {
    let value = Number(event.value) / SLIDER_BASE

    if ('keydown' === event.originalEvent.type) {
      const native = event.originalEvent as React.KeyboardEvent
      if (('ArrowRight' === native.key || 'ArrowUp' === native.key) && value < max) {
        value = value + step > max ? max : value + step
      } else if (('ArrowLeft' === native.key || 'ArrowDown' === native.key) && value > min) {
        value = value - step < min ? min : value - step
      }
    }

    if (!touched) {
      setTouched(true)
      if ('function' == typeof onTouch) onTouch()
    }
    if (!dragging) setDragging(true)
    const fixedValue = Number((remaining != null && value > remaining * max ? remaining * max : value).toFixed(2))

    setLocalValue(fixedValue)
    currentValue.current = fixedValue
    // onChange(fixedValue)
    onChange(Number((Math.round(fixedValue / step) * step).toFixed(2)))
  }

  const onChangeArray = (event: SliderChangeParams) => {
    const value = event.value

    if (!touched) {
      setTouched(true)
      if ('function' == typeof onTouch) onTouch()
    }
    if ('keydown' === event.originalEvent.type && Array.isArray(value)) {
      if (!keyboardChange) {
        setKeyboardChange(true)
      }
      const native = event.originalEvent as React.KeyboardEvent
      const increaseValue = 'ArrowRight' === native.key || 'ArrowUp' === native.key
      const decreaseValue = 'ArrowLeft' === native.key || 'ArrowDown' === native.key

      if (!currentValue.current) currentValue.current = 0
      if (increaseValue && currentValue.current < max) {
        currentValue.current = currentValue.current + 1
      } else if (decreaseValue && currentValue.current > min) {
        currentValue.current = currentValue.current - 1
      }

      const response: SliderValueType =
        0 === currentValue.current
          ? [0, 0]
          : currentValue.current < 0
          ? [currentValue.current * SLIDER_BASE, 0]
          : [0, currentValue.current * SLIDER_BASE]

      setLocalArray(response)
      onChange(Math.round((response[0] < 0 ? response[0] : response[1]) / SLIDER_BASE))
    } else {
      setLocalArray(value as [number, number])
    }
  }

  const onEndChangeArray = (sliderValue: SliderValueType) => {
    if (Array.isArray(sliderValue)) {
      setDragging(false)
      if ('function' == typeof onSlideEnd) onSlideEnd()
      const leftChanged = sliderValue[0] == localBackUpArray[0]
      const rightChanged = sliderValue[0] >= 0 && sliderValue[1] == localBackUpArray[1]
      const fixedVal =
        sliderValue[0] < 0
          ? leftChanged
            ? sliderValue[1]
            : sliderValue[0]
          : rightChanged
          ? sliderValue[0]
          : sliderValue[1]
      const val = Math.round(fixedVal / SLIDER_BASE)
      setDragging(true)
      setTimeout(() => {
        setLocalArray(sliderValue[0] < 0 && val <= 0 ? [val * SLIDER_BASE, 0] : [0, val * SLIDER_BASE])
        setLocalBackUpArray(sliderValue[0] < 0 && val <= 0 ? [val * SLIDER_BASE, 0] : [0, val * SLIDER_BASE])
        currentValue.current = val
        onChange(val)
        setDragging(false)
        setKeyboardChange(false)
      }, 50)
    }
  }

  const getLabel = (value: number) => {
    if (!touched && (valueFromCenter || 'center' == start)) return t('questions', 'mx', 'dragSlider')
    if (labelTemplate) return labelTemplate(Math.round(value / SLIDER_BASE), 0)
    return ''
  }

  return (
    <div
      ref={sliderRef}
      className={`${style.slider} ${viewport.mobile ? style.mobile : ''} aytmSlider`}
      onTouchStart={() => hapticEffects.soft()}
    >
      {valueFromCenter ? (
        <PrimeSlider
          disabled={disabled}
          aria-label={answerText}
          value={localArray}
          aria-valuetext={label}
          min={min * SLIDER_BASE || 0}
          max={max * SLIDER_BASE || 100}
          step={step}
          className={`${className} ${transparentBackground ? style.transparent : ''} ${
            Array.isArray(localArray) && localArray[1] ? style.hiddenFirst : style.hiddenSecond
          } ${'center' === start && !touched ? style.centeredHandle : ''}`}
          labelTemplate={getLabel}
          onChange={event => onChangeArray(event)}
          onSlideEnd={event => onEndChangeArray(event.value)}
          range
        />
      ) : (
        <PrimeSlider
          disabled={disabled}
          aria-valuetext={label}
          aria-label={answerText}
          value={null == localValue ? 1 : localValue * SLIDER_BASE}
          min={min * SLIDER_BASE || 0}
          max={max * SLIDER_BASE || 100}
          step={step}
          className={`${className} ${ticks ? style.withTicks : ''} ${dragging ? style.dragging : ''} ${
            'center' === start && !touched ? style.centeredHandle : ''
          }`}
          onChange={event => onChangeValue(event)}
          onSlideEnd={event => onEndChangeValue(convertValue(event))}
          labelTemplate={getLabel}
        />
      )}
    </div>
  )
}
